import EmailValidator from "email-validator";
import {
  isEmpty,
  isObject,
  isInteger,
  isEqual,
  trim,
  every,
  sumBy,
  compact
} from "lodash";

import { errorMessages } from "./errorMessages";
import {
  BUDGET_DISPATCHER,
  CHECKBOXES,
  CHECKBOXES_MULTI_SELECT,
  CHECKBOXES_SELECT,
  EVENTS,
  FILE,
  FILES,
  HTML,
  ORAL_INTERVIEW,
  RADIOS,
  SORTABLE_LIST,
  THEMES
} from "../components/Form/SchemaTypes";
import { UPLOAD_STATUS_IN_PROGRESS } from "../components/Uploader/useDirectFileUpload";

const isFilledForType = {
  // Check that at least one option has been checked in the checkboxes field
  // or that we checked the `other` field and filled the `otherValue` field.
  [CHECKBOXES](value) {
    return !isEmpty(value.values) || (value.other === true && value.otherValue);
  },

  // Check that a value has been selected or that we used the `other` and
  // `otherValue` fields
  [RADIOS](value) {
    return value.value || (value.other === true && value.otherValue);
  },

  [FILE](value) {
    return !isEmpty(value.file?.key);
  },

  // Check that we have key values
  [FILES](value) {
    return (
      value.files &&
      value.files.length &&
      every(value.files, file => file.key && file.status !== UPLOAD_STATUS_IN_PROGRESS)
    );
  },

  [HTML](value) {
    return !(
      isEmpty(value.value) ||
      isEmpty(trim(value.value)) ||
      // Remove <p>, </p> and <br /> tags and trim it to check if it needs
      // to be filled.
      isEmpty(trim(value.value.replace(/<\/?(p|br)\s*\/?>/g, "")))
    );
  },

  [ORAL_INTERVIEW](value) {
    return value.hasUserMessage;
  },

  [BUDGET_DISPATCHER](value) {
    const totalSpentBudget = sumBy(value.values, weightedOption => weightedOption.value);

    return totalSpentBudget > 0;
  },

  [SORTABLE_LIST](value) {
    const { values = [], optionsCount = 0 } = value || {};

    return values.length === optionsCount;
  },

  [THEMES](value) {
    return compact(value?.values || []).length > 0;
  },

  [CHECKBOXES_SELECT](value) {
    return !isEmpty(value?.value?.value) || (value.other === true && value.otherValue);
  },

  [CHECKBOXES_MULTI_SELECT](value) {
    return isFilledForType.checkboxes(value);
  },

  [EVENTS](value) {
    return !isEmpty(value.values) || !isEmpty(value.value?.value);
  }
};

function isFilled(value, type) {
  if (!value) return false;

  if (isFilledForType[type]) {
    return isFilledForType[type](value);
  } else {
    return value && !isEmpty(value) && !isEmpty(value.value);
  }
}

export function required(value, params, fieldName, _fields, schema) {
  if (!isFilled(value || params.value, schema[fieldName].type)) {
    return { [fieldName]: params.errorMessage || errorMessages.required };
  }
}

export function length(value, params, fieldName) {
  // Only applicable to string fields with a `.value` property containing the
  // string at the moment.
  //
  // Fallback to an empty string if the provided value is null or undefined to avoid
  // empty input errors
  const targetValue =
    (typeof value?.value !== "undefined" ? value.value : value?.values) || "";

  // Only validate length if the value is not empty. If there's a minimum length and the
  // `required` validation is already set, we let the empty string case be handled by the
  // `required` validation, avoiding useless double validation error display to the user.
  if (isEmpty(targetValue)) return;

  if (!isObject(params) || !(isInteger(params.minimum) || isInteger(params.maximum))) {
    throw new Error(
      "You need to provide a minimum or maximum param to the length " +
        "validation. The error occured while trying to validate the field : " +
        fieldName
    );
  }

  const errors = [];

  // Adapt the error message to the type of the field
  const valueIsAString = typeof targetValue === "string";

  if (isInteger(params.minimum) && targetValue.length < params.minimum) {
    if (params.tooShortMessage) {
      errors.push(params.tooShortMessage);
    } else if (valueIsAString) {
      errors.push(errorMessages.stringLengthTooShort(params.minimum));
    } else {
      errors.push(errorMessages.lengthTooShort);
    }
  }

  if (isInteger(params.maximum) && targetValue.length > params.maximum) {
    if (params.tooLongMessage) {
      errors.push(params.tooLongMessage);
    } else if (valueIsAString) {
      errors.push(errorMessages.stringLengthTooLong(params.maximum));
    } else {
      errors.push(errorMessages.lengthTooLong);
    }
  }

  return { [fieldName]: errors };
}

export function otherValueRequired(value, _params, fieldName) {
  if (value?.other && !value?.otherValue) {
    return { [fieldName]: errorMessages.otherValueRequired };
  }
}

export function confirmationOf(value, otherFieldName, fieldName, fields) {
  if (!isEqual(value, fields[otherFieldName])) {
    return { [fieldName]: errorMessages.mustCorrespond };
  }
}

export function budgetSpent(value, params, fieldName) {
  const errors = [];

  const totalSpentBudget = sumBy(value.values, weightedOption => weightedOption.value);
  const { minBudgetToSpend } = params;

  if (isInteger(minBudgetToSpend) && totalSpentBudget < minBudgetToSpend) {
    errors.push(errorMessages.notEnoughBudgetSpent);
  }

  return { [fieldName]: errors };
}

export function budgetFilled(_, { answers }, fieldName, fields) {
  const errors = [];
  const fieldNames = answers.map(answer => answer.formField.fieldName);

  const hasSelectedBudgetLine = fieldNames.some(fieldName => {
    const budgetLine = fields[fieldName];

    if (!budgetLine || isEmpty(budgetLine.values)) {
      return false;
    }

    // Check that at least one budget line value has optionIds
    return budgetLine.values.some(value => {
      return value.optionIds && value.optionIds.length > 0;
    });
  });

  if (!hasSelectedBudgetLine) {
    errors.push(errorMessages.budgetMustBeFilled);
  }

  return { [fieldName]: errors };
}

export function budgetGoal(_, { goal, unit, answers }, fieldName, fields) {
  const errors = [];
  const fieldNames = answers.map(answer => answer.formField.fieldName);

  // Calculate the total budget from all chapters
  const total = fieldNames.reduce((sum, chapterFieldName) => {
    const chapter = fields[chapterFieldName];
    return sum + (chapter?.total || 0);
  }, 0);

  // Validation fails if total is less than goal
  if (total < goal) {
    errors.push(errorMessages.budgetGoalNotReached(goal, unit));
  }

  return { [fieldName]: errors };
}

export function optionsSelectedPerItem({ values }, params, fieldName) {
  const errors = [];
  const { minimum, maximum, itemIds } = params;

  const notEnoughOptionsSelectedMessage =
    errorMessages.notEnoughOptionsSelectedPerItem(minimum);
  const tooManyOptionsSelectedMessage =
    errorMessages.tooManyOptionsSelectedPerItem(maximum);

  itemIds.forEach(itemId => {
    const optionsSelected = values.filter(itemOption => itemOption.itemId === itemId);

    if (
      isInteger(minimum) &&
      optionsSelected.length < minimum &&
      errors.indexOf(notEnoughOptionsSelectedMessage) === -1
    ) {
      errors.push(notEnoughOptionsSelectedMessage);
    }

    if (
      isInteger(maximum) &&
      optionsSelected.length > maximum &&
      errors.indexOf(tooManyOptionsSelectedMessage) === -1
    ) {
      errors.push(tooManyOptionsSelectedMessage);
    }
  });

  return { [fieldName]: errors };
}

export function email(value, _params, fieldName) {
  // Use the EmailValidator package to validate the e-mail, as it seems to use a RFC5322
  // compliant regex just like we do in the backend, which avoid having false-positives
  // in the front that would make the backend fail to validate the e-mail
  if (value.value && !EmailValidator.validate(value.value)) {
    return { [fieldName]: errorMessages.invalidEmailFormat };
  }
}

export function uploadsComplete(value, _params, fieldName) {
  if (
    (value.files &&
      value.files.some(file => file.status === UPLOAD_STATUS_IN_PROGRESS)) ||
    (value.file && value.file.status === UPLOAD_STATUS_IN_PROGRESS)
  ) {
    return { [fieldName]: errorMessages.uploadsIncomplete };
  }
}
